import type { Dispatch } from 'redux'

import type { RecipeCoreId } from '@library/menu-service'
import type { Box } from '@library/menu-service/queries/menuApiBoxes/types'

import { actionTypes } from 'actions/actionTypes'
import { trackUserAddRemoveRecipe } from 'actions/loggingmanager'
import * as trackingKeys from 'actions/trackingKeys'
import { getIsAuthenticated } from 'selectors/auth'
import {
  getBasketMenuId,
  getBasketMenuUuid,
  getBasketRecipes,
  getNumPortions,
} from 'selectors/basket'
import { getAvailableBoxTypes } from 'selectors/menu-boxes/boxes'
import { getCurrentCollectionId } from 'selectors/menu-collections/getCurrentCollectionId'
import { getAvailableRecipes } from 'selectors/menu-recipes/getAvailableRecipes'
import { getRecipeIdByCoreId } from 'selectors/menu-recipes/getRecipeIdByCoreId'
import { getRecipeIdByCoreIdMap } from 'selectors/menu-recipes/getRecipeIdByCoreIdMap'
import { basketSum } from 'utils/basket'

const DEFAULT_MAX_RECIPES = 4

type State = any
type GetState = () => State

const getMaxRecipesForPortion = (state: State, numberOfPortions = 2) => {
  const boxes = getAvailableBoxTypes(state)
  const filteredBoxes = boxes.filter((box: Box) => box.numberOfPortions === numberOfPortions)
  const recipeCounts = filteredBoxes.map((box: Box) => box.numberOfRecipes)

  return recipeCounts.length > 0 ? Math.max(...recipeCounts) : DEFAULT_MAX_RECIPES
}

const getRecipeLimitReached = (state: State) => {
  const recipesInBasket = getBasketRecipes(state)
  const maxRecipesNum = getMaxRecipesForPortion(state, getNumPortions(state))
  const isAuthenticated = getIsAuthenticated(state)

  return (
    !isAuthenticated &&
    basketSum(recipesInBasket as unknown as Parameters<typeof basketSum>[0]) >= maxRecipesNum
  )
}

const removeRecipeFromBasket =
  (recipeId: RecipeCoreId) => (dispatch: Dispatch<any>, getState: GetState) => {
    const state = getState()
    const recipesInBasket = getBasketRecipes(state)
    const reachedLimit = getRecipeLimitReached(state)
    const recipeIdByCoreIdMap = getRecipeIdByCoreIdMap(state)

    const basketRecipesWithoutRecipeId = Object.keys(recipesInBasket.toJS()).filter(
      (key) => key !== recipeId,
    )

    dispatch({
      type: actionTypes.BASKET_RECIPE_REMOVE,
      recipeId,
      markUnsaved: true,
      trackingData: {
        actionType: trackingKeys.removeRecipe,
        basketRecipes: basketRecipesWithoutRecipeId,
        boxSize: getNumPortions(state),
        boxType: 'chosen',
        menu_id: getBasketMenuId(state),
        menu_uuid: getBasketMenuUuid(state),
        recipe_id: recipeId,
        recipe_uuid: recipeIdByCoreIdMap[recipeId],
        view: undefined,
        position: undefined,
        collection: getCurrentCollectionId(state),
        basket_recipe_count: recipesInBasket.size - 1,
      },
    })

    if (!reachedLimit) {
      dispatch({
        type: actionTypes.BASKET_LIMIT_REACHED,
        limitReached: reachedLimit,
        trackingData: {
          actionType: trackingKeys.basketLimit,
          limitReached: reachedLimit,
          view: undefined,
          source: actionTypes.RECIPE_REMOVED,
        },
      })
    }

    dispatch(trackUserAddRemoveRecipe())
  }

export const removeMissingRecipesFromBasket =
  () => (dispatch: Dispatch<any>, getState: GetState) => {
    const state = getState()
    const basketRecipes = getBasketRecipes(state)
    const availableRecipes = getAvailableRecipes(state)
    const basketRecipeAmounts = basketRecipes.toJS() as Record<RecipeCoreId, number>

    Object.entries(basketRecipeAmounts).forEach(([coreId, amount]) => {
      const recipeCoreId = coreId as RecipeCoreId
      const uuid = getRecipeIdByCoreId(recipeCoreId)(state)

      if (!uuid || !(uuid in availableRecipes)) {
        for (let i = 0; i < amount; i++) {
          dispatch(removeRecipeFromBasket(recipeCoreId))
        }
      }
    })
  }
